/**
 * OS repairs, never only refuses (L64.4 of docs/plans/ledger.md): every code
 * a save or a confirm can refuse with (an entry, a money rule, a reference, a
 * required field, an approval, a closed month) names how OS mends it.
 *
 *   add_row     a row the entry names is not on its list (a category, an
 *               Accounts row, an item, a party): OS proposes the row, with
 *               its required choices filled, then the entry
 *   fill_field  a field the entry left empty that has a sensible start: OS
 *               fills it and shows it on the card
 *   fix_setup   the setup is the cause (a rule reading the wrong field, a
 *               role reading an optional one, a choice that is not there):
 *               OS proposes a setup fix to whoever may change the setup
 *   ask         a value only the person knows: one short question, batched
 *   none        nothing to mend, for the reason it names: the request was
 *               said wrong (OS says it again), a right the person lacks (OS
 *               offers Ask the owner), a closed month (Correct is the way
 *               on), a decision already made, or a row that moved
 *
 * A fix the person may not make (no grant to add to that list, no
 * edit_structure for a setup fix) becomes Ask the owner at run time, never a
 * different kind here. apps/server/src/manifest-fix-kinds.enforcement.test.ts
 * reads every code those paths give and fails on one with no fix kind, so no
 * new code is a dead end.
 */
export const FIX_KINDS = ['add_row', 'fill_field', 'fix_setup', 'ask', 'none'] as const;
export type FixKind = (typeof FIX_KINDS)[number];

/** Why a refusal has nothing to mend. */
export const NO_FIX_REASONS = ['wire', 'right', 'closed', 'decided', 'moved', 'not_built'] as const;
export type NoFixReason = (typeof NO_FIX_REASONS)[number];

export type RefusalFix = { readonly kind: Exclude<FixKind, 'none'> } | { readonly kind: 'none'; readonly why: NoFixReason };

const each = (codes: readonly string[], fix: RefusalFix): Record<string, RefusalFix> => Object.fromEntries(codes.map((code) => [code, fix]));

const ADD_ROW: RefusalFix = { kind: 'add_row' };
const FILL: RefusalFix = { kind: 'fill_field' };
const SETUP: RefusalFix = { kind: 'fix_setup' };
const ASK: RefusalFix = { kind: 'ask' };
const none = (why: NoFixReason): RefusalFix => ({ kind: 'none', why });

/** Each save or confirm refusal code, and how OS mends it. */
export const REFUSAL_FIX: Readonly<Record<string, RefusalFix>> = {
  // A row the entry names that its list does not hold.
  // ROW_YOURS: a draft asked to wait for a row its writer may add themselves (L64.5); they add it.
  ...each(['REFERENCE_UNKNOWN', 'VALUE_NOT_IN_LIST', 'ROW_UNKNOWN', 'CATEGORY_NEEDED', 'ACCOUNT_UNKNOWN', 'PARTY_UNKNOWN', 'ROW_YOURS'], ADD_ROW),
  // A field left empty: filled from its start, or asked.
  ...each(['FIELD_REQUIRED', 'ACCOUNT_TYPE_MISSING'], FILL),
  // The setup is the cause (B11, G14, G17, G19): a rule reading the wrong field or an optional one, a choice not there.
  ...each(
    [
      'OPTION_UNKNOWN',
      'RULE_ROLE_MISSING',
      'RULE_ROLE_UNKNOWN',
      'RULE_ACCOUNT_UNKNOWN',
      'RULE_CHOICE_UNMAPPED',
      'RULE_CHOICE_UNKNOWN',
      'RULE_PATTERN_UNKNOWN',
      'RULE_PARTY_UNKNOWN',
      'RULE_FIELD_TYPE',
      'RULE_FIELD_UNKNOWN',
      'POSTING_UNBALANCED',
      'POSTING_ONE_LINE',
      'MONEY_RULE_HELD',
      'FIELD_MISCONFIGURED',
      'APPROVAL_CHAIN_INVALID',
    ],
    SETUP,
  ),
  // A value only the person knows: which of two, which parent, what to drop, a reason, a number of their own.
  ...each(
    [
      'REFERENCE_AMBIGUOUS',
      'FIELD_AMBIGUOUS',
      'FILTER_MISMATCH',
      'TOO_MANY_CHOSEN',
      'LINES_OVER_MAX',
      'LINES_HIDDEN',
      'FIELD_LOCKED',
      'FIELD_LOCKED_HELD',
      'ENTRY_RULE_BROKEN',
      'SERIES_NUMBER_TAKEN',
      'ACCOUNT_NOT_ALLOWED',
      'PARTY_PERIOD_OVERLAP',
      'PARTY_PERIOD_BACKWARDS',
      'APPROVAL_REASON_NEEDED',
      'CLOSE_TO_SORT',
    ],
    ASK,
  ),
  // Said wrong: OS says it again, the way the problem names.
  ...each(
    [
      'FIELD_UNKNOWN',
      'FIELD_REPEATED',
      'FIELD_COMPUTED',
      'VALUE_TYPE',
      'VALUE_UNREADABLE',
      'LINES_IN_VALUES',
      'LINES_UNKNOWN',
      'LINE_SERVER_SET',
      'LINES_DIRECT',
      'TOO_MANY_LINES',
      'ENTRY_INVALID',
      'ENTRIES_INVALID',
      'ENTRIES_REFUSED',
      'ENTRY_UNREADABLE',
      'NOTHING_TO_CHANGE',
      'BAD_BODY',
      'BAD_REQUEST',
      'BAD_ENTRY_ID',
      'BAD_RECORD_ID',
      'BAD_CLIENT_OP_ID',
      'BAD_BASE_VERSION',
      'BAD_BATCH_SIZE',
      'BAD_BEFORE',
      'BAD_CURSOR',
      'BAD_EXPORT',
      'BAD_LINE_ID',
      'BAD_LINES',
      'DUPLICATE_ENTRY_ID',
      'DUPLICATE_LINE_ID',
      'ENTRY_ID_CONFLICT',
      'TENANT_REQUIRED',
      'APPROVAL_CHAIN_MOVED',
      'BAD_ROW_WAIT',
    ],
    none('wire'),
  ),
  // A right the person lacks: OS offers Ask the owner, and never says what they may not see.
  ...each(['FIELD_HIDDEN', 'FIELD_NOT_WRITABLE', 'DIRECTION_NOT_ALLOWED', 'NOT_ALLOWED', 'NOT_YOURS', 'APPROVAL_NOT_YOURS', 'APPROVAL_NOT_WRITER', 'APPROVAL_OWN_ENTRY', 'APPROVAL_ONE_STEP'], none('right')),
  // A closed month stays closed: Correct is the way on (L12).
  ...each(['PERIOD_CLOSED', 'ROLLUP_PERIOD_CLOSED', 'ENTRY_NOT_CLOSED', 'ENTRY_ALREADY_CORRECTED', 'ENTRY_IS_CORRECTION', 'ENTRY_IS_DERIVED', 'CLOSE_ENTRIES_WAITING'], none('closed')),
  // Decided already: money counted in an account stays, a given number never changes, a decision is made.
  ...each(['ACCOUNT_IN_USE', 'PARTY_IN_USE', 'ACCOUNT_TYPE_LOCKED', 'SERIES_NUMBER_GIVEN', 'APPROVAL_NOT_WAITING'], none('decided')),
  // The row or record is gone or moved since the card was drawn.
  // ROW_EXISTS: the row a draft would wait for is there now (L64.5); the entry picks it.
  ...each(['ENTRY_UNKNOWN', 'ENTRY_MOVED', 'RECORD_UNKNOWN', 'NOT_AN_ACCOUNT', 'ROW_EXISTS'], none('moved')),
  ...each(['LINES_TOO_DEEP'], none('not_built')),
};

/** How OS mends one refusal code; null for a code no table names (the enforcement test keeps that empty). */
export const refusalFix = (code: string): RefusalFix | null => REFUSAL_FIX[code] ?? null;

/** The fix kind of one code: `none` for a code no table names. */
export const fixKindOf = (code: string): FixKind => refusalFix(code)?.kind ?? 'none';
